/**
 * The shape of the app's navigation, in one place.
 *
 * The primary bar names a *section*; the second tier names the pages inside it.
 * That grouping is the point: "Run history" (PBIRS) and "Export history"
 * (KT Insights) are the same idea for two different engines, and a flat bar made
 * you know which engine produced a report before you could go looking for it.
 * Under a section the choice is implicit, and the two siblings can finally share
 * a word.
 *
 * Section membership is declared here rather than inferred from the URL, because
 * it does not follow the URL: /history is a Subscriptions page. Paths are left
 * exactly where they were — this changes presentation, not routing — so the
 * AppBar and the breadcrumbs both read the answer from this model instead of
 * each re-deriving it from the path and disagreeing.
 */

import type { Features } from '@/types';

export type NavItem = {
  label: string;
  to: string;
  /** Breadcrumb wording, where the tab has to be shorter than the page's title. */
  crumb?: string;
};

export type NavSection = {
  key: string;
  label: string;
  to: string;
  /** Second-tier items. Omitted for a section that is a single page. */
  children?: NavItem[];
  devOnly?: boolean;
  adminOnly?: boolean;
  /**
   * Hidden unless this feature is switched on for the caller. Separate from `adminOnly` because
   * they answer different questions — "may this person use it" against "does this deployment have
   * it at all" — and a half-finished feature has to be invisible to the admins who WILL own it.
   *
   * Presentation only. The routes in App.tsx are gated on the same flag and the endpoints behind
   * them enforce it server-side; this stops the tab appearing, nothing more.
   */
  featureFlag?: keyof Features;
};

export const navSections: NavSection[] = [
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    to: '/subscriptions',
    children: [
      { label: 'My subscriptions', to: '/subscriptions' },
      { label: 'Run history', to: '/history' },
      { label: 'Create new', to: '/subscriptions/new', crumb: 'New subscription' }
    ]
  },
  {
    key: 'ktinsights',
    label: 'KT Insights',
    to: '/ktinsights',
    children: [
      { label: 'Scheduled exports', to: '/ktinsights' },
      { label: 'Export history', to: '/ktinsights/history' }
    ]
  },
  { key: 'pickup', label: 'Report Pickup', to: '/pickup' },
  { key: 'outbox', label: 'Outbox', to: '/outbox', devOnly: true },
  { key: 'admin', label: 'Admin', to: '/admin', adminOnly: true },
  {
    // Its own section rather than a child of Admin, even though it lives under /admin: a child
    // cannot be feature-gated on its own here, and the pack screens are two tiers deep in their own
    // right. Declared after Admin so that matchNav's deepest-wins rule gives /admin/packs to this
    // section rather than to Admin, which also covers the path.
    key: 'packs',
    label: 'Report packs',
    to: '/admin/packs',
    adminOnly: true,
    featureFlag: 'reportPacks',
    children: [
      { label: 'Pack library', to: '/admin/packs' },
      { label: 'New pack', to: '/admin/packs/new', crumb: 'New pack' }
    ]
  },
  {
    // Same shape as Report packs, for the same reasons: lives under /admin, declared after Admin so
    // deepest-wins gives /admin/custom-views to this section, feature-gated on its own flag.
    key: 'customviews',
    label: 'Custom views',
    to: '/admin/custom-views',
    adminOnly: true,
    featureFlag: 'customViews',
    children: [
      { label: 'View library', to: '/admin/custom-views' },
      { label: 'New view', to: '/admin/custom-views/new', crumb: 'New view' }
    ]
  },
  // Last, where people look for it, and for everyone: the guides are written for account managers,
  // who are the readers least likely to have someone to ask.
  { key: 'help', label: 'Help', to: '/help' }
];

/**
 * The sections this caller may see.
 *
 * `features` is optional and a missing flag is OFF, never on. The profile call on an older API
 * build carries no `features` object at all, and treating that as "everything enabled" would put a
 * tab in the bar for endpoints that build does not serve.
 */
export function visibleSections({ devMode, isAdmin, features }: {
  devMode: boolean;
  isAdmin: boolean;
  features?: Features;
}) {
  return navSections.filter(s =>
    (!s.devOnly || devMode)
    && (!s.adminOnly || isAdmin)
    && (!s.featureFlag || features?.[s.featureFlag] === true));
}

/** Does `pathname` sit at or below `to`? "/subscriptions" must not swallow "/subscriptions-archive". */
function covers(to: string, pathname: string) {
  return pathname === to || pathname.startsWith(to.endsWith('/') ? to : `${to}/`);
}

/**
 * The deepest declared entry covering this path, and the section it belongs to.
 * Deepest wins so /subscriptions/new resolves to its own tab rather than to the
 * section root that also covers it.
 */
export function matchNav(pathname: string): { section?: NavSection; item?: NavItem } {
  let section: NavSection | undefined;
  let item: NavItem | undefined;
  let best = -1;

  for (const s of navSections) {
    const candidates: NavItem[] = [{ label: s.label, to: s.to }, ...(s.children ?? [])];
    for (const candidate of candidates) {
      if (!covers(candidate.to, pathname) || candidate.to.length <= best) continue;
      best = candidate.to.length;
      section = s;
      item = candidate;
    }
  }

  return { section, item };
}
